import { User } from "@src/domain/users/entities/user.entity";
import { Role } from "@src/domain/users/entities/role.entity";

/**
 * User Mapper
 * Maps between API responses and User domain entities
 */
export class UserMapper {
    /**
     * Map API response to User domain entity
     */
    static toDomain(raw: any): User {
        // Helper function to safely parse dates
        const parseDate = (dateValue: any): Date => {
            if (!dateValue) {
                return new Date();
            }
            const parsedDate = new Date(dateValue);
            // Check if date is valid
            if (isNaN(parsedDate.getTime())) {
                return new Date();
            }
            return parsedDate;
        };

        return User.create({
            userId: raw.user_id,
            email: raw.email,
            firstName: raw.first_name,
            middleName: raw.middle_name || null,
            lastName: raw.last_name,
            displayName: raw.display_name,
            fullName: raw.full_name,
            profilePictureUrl: raw.profile_picture_url || null,
            phone: raw.phone_number || raw.phone || "",
            isActive: raw.is_active,
            isVerified: raw.is_verified,
            isBanned: raw.is_banned,
            createdAt: parseDate(raw.created_at),
            updatedAt: parseDate(raw.updated_at),
            universityId: raw.university_id || null,
            roles: raw.roles ? raw.roles.map((role: any) => this.roleToDomain(role)) : [],
            permissions: raw.permissions || [],
            profileState: raw.profile_state,
            whatsappLinked: raw.whatsapp_linked,
        });
    }

    /**
     * Map Role API response to Role domain entity
     */
    static roleToDomain(raw: any): Role {
        // Helper function to safely parse dates
        const parseDate = (dateValue: any): Date => {
            if (!dateValue) {
                return new Date();
            }
            const parsedDate = new Date(dateValue);
            // Check if date is valid
            if (isNaN(parsedDate.getTime())) {
                return new Date();
            }
            return parsedDate;
        };

        return Role.create({
            roleId: raw.role_id,
            name: raw.name,
            description: raw.description || "",
            isActive: raw.is_active ?? true,
            permissions: raw.permissions || [],
            createdAt: parseDate(raw.created_at),
            updatedAt: parseDate(raw.updated_at),
        });
    }

    /**
     * Map User domain entity to API persistence format
     */
    static toPersistence(user: User): any {
        return {
            user_id: user.userId,
            email: user.email,
            first_name: user.firstName,
            middle_name: user.middleName,
            last_name: user.lastName,
            display_name: user.displayName,
            full_name: user.fullName,
            profile_picture_url: user.profilePictureUrl,
            phone_number: user.phone,
            is_active: user.isActive,
            is_verified: user.isVerified,
            is_banned: user.isBanned,
            created_at: user.createdAt.toISOString(),
            updated_at: user.updatedAt.toISOString(),
            university_id: user.universityId,
            roles: user.roles.map(role => ({
                role_id: role.roleId,
                name: role.name,
                description: role.description,
                is_active: role.isActive,
                permissions: role.permissions,
                created_at: role.createdAt.toISOString(),
                updated_at: role.updatedAt.toISOString(),
            })),
            permissions: user.permissions,
            profile_state: user.profileState,
            whatsapp_linked: user.whatsappLinked,
        };
    }
}
